import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Zap, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature context
const FeaturesContext = createContext(null);

// Feature info mapping
const FEATURE_INFO = {
  email_notifications: { name: 'Email Notifications', minTier: 'free', icon: '📧' },
  quick_stats_dashboard: { name: 'Quick Stats Dashboard', minTier: 'basic', icon: '📊' },
  unified_view: { name: 'Unified dashboard', minTier: 'basic', icon: '📅' },
  document_attachments: { name: 'Document Attachments', minTier: 'pro', icon: '📎' },
  tc_role_enabled: { name: 'Transaction Coordinator Role', minTier: 'pro', icon: '👥' },
  broker_dashboard: { name: 'Team home', minTier: 'team', icon: '📈' },
  api_access: { name: 'API Access', minTier: 'enterprise', icon: '🔌' },
  sso_enabled: { name: 'SSO/SAML', minTier: 'enterprise', icon: '🔐' },
  white_label: { name: 'White Label Branding', minTier: 'enterprise', icon: '🏷️' }
};

export function FeaturesProvider({ children, token }) {
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeatures = useCallback(async () => {
    if (!token) {
      setFeatures(null);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API}/features/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFeatures(data.features);
        setError(null);
      } else {
        setFeatures(null);
      }
    } catch (err) {
      console.error('Error fetching features:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Check if a specific feature is enabled
  const hasFeature = useCallback((featureName) => {
    if (!features) return false;
    return features[featureName] === true;
  }, [features]);

  // Get the current tier
  const currentTier = features?.tier_id || features?.plan_id || features?.tier || 'free';

  // Check if user has organization
  const hasOrganization = features?.has_organization || false;

  // Get feature info
  const getFeatureInfo = useCallback((featureName) => {
    return FEATURE_INFO[featureName] || { name: featureName, minTier: 'pro', icon: '✨' };
  }, []);

  return (
    <FeaturesContext.Provider value={{
      features,
      loading,
      error,
      hasFeature,
      currentTier,
      hasOrganization,
      getFeatureInfo,
      refreshFeatures: fetchFeatures
    }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  const context = useContext(FeaturesContext);
  if (!context) {
    // Return a fallback for components outside the provider
    return {
      features: null,
      loading: false,
      error: null,
      hasFeature: () => false,
      currentTier: 'free',
      hasOrganization: false,
      getFeatureInfo: (f) => FEATURE_INFO[f] || { name: f, minTier: 'pro', icon: '✨' },
      refreshFeatures: () => {}
    };
  }
  return context;
}

// Feature gate component - shows children only if feature is enabled
export function FeatureGate({ feature, fallback = null, children }) {
  const { hasFeature, loading } = useFeatures();
  
  if (loading) return null;
  
  if (!hasFeature(feature)) {
    return fallback;
  }
  
  return children;
}

// Upgrade prompt component - clean inline upgrade card
export function UpgradePrompt({ feature, minTier = 'pro', message, compact = false }) {
  const navigate = useNavigate();
  const featureInfo = FEATURE_INFO[feature] || { name: feature, minTier };
  
  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <Lock className="w-4 h-4 text-amber-400" />
        <span className="text-amber-300 text-sm">{message || `${featureInfo.name} requires ${minTier}`}</span>
        <Button 
          size="sm" 
          onClick={() => navigate('/settings?tab=subscription')}
          className="ml-auto bg-amber-600 hover:bg-amber-700 text-xs h-7"
        >
          Upgrade
        </Button>
      </div>
    );
  }
  
  return (
    <div className="bg-gradient-to-br from-muted to-background border border-border rounded-xl p-6 text-center">
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
        <Lock className="w-6 h-6 text-amber-400" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {featureInfo.name}
      </h3>
      <p className="text-muted-foreground mb-4 text-sm">
        {message || `This feature requires a ${minTier.charAt(0).toUpperCase() + minTier.slice(1)} plan or higher.`}
      </p>
      <Button 
        onClick={() => navigate('/settings?tab=subscription')}
        className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-700"
      >
        <Zap className="w-4 h-4 mr-2" />
        Upgrade Now
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

// Full page upgrade prompt for feature-gated pages
export function FeatureGatedPage({ feature, title, description, children }) {
  const { hasFeature, loading, currentTier, getFeatureInfo } = useFeatures();
  const navigate = useNavigate();
  const featureInfo = getFeatureInfo(feature);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!hasFeature(feature)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">
            {title || featureInfo.name}
          </h1>
          <p className="text-muted-foreground mb-6">
            {description || `This feature requires a ${featureInfo.minTier.charAt(0).toUpperCase() + featureInfo.minTier.slice(1)} plan or higher. You're currently on the ${currentTier} plan.`}
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/settings?tab=subscription')}
              className="w-full bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-700"
            >
              <Zap className="w-4 h-4 mr-2" />
              Upgrade to {featureInfo.minTier.charAt(0).toUpperCase() + featureInfo.minTier.slice(1)}
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="w-full text-muted-foreground hover:text-foreground"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
}

export default FeaturesContext;
